import { Box } from "@mui/material"
import { useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React, { useEffect } from "react"
import { useFormContext } from "react-hook-form"

import { useBankCompanies } from "../../hooks/useBankCompanies"
import type { PaymentMethodFormValues } from "../../schemas/PaymentMethodSchema"
import { BankDetailsFields } from "../BankDetailsFields"

import BankLocationSelector from "./BankLocationSelector"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import type { CompanyPaymentType } from "@/graphql/codegen/graphql"

interface Props {
  paymentTypeToCreateFrom?: CompanyPaymentType | null
}

const BankingMethodFields: FC<Props> = ({ paymentTypeToCreateFrom }) => {
  const searchParams = useSearch({ from: "/app/companies/$companyId/edit/_layout/accounting" })
  const methods = useFormContext<PaymentMethodFormValues>()
  const { watch, setValue } = methods
  const {
    banks,
    isLoading: isBanksLoading,
    setSearchTerm: setBankSearchTerm,
  } = useBankCompanies({ withLocations: true })

  const watchedBankId = watch("bankId")

  // Reset bank location when bank changes
  useEffect(() => {
    setValue("bankLocationId", "")
  }, [watchedBankId, setValue])

  return (
    <Box>
      <AutoComplete
        control={methods.control}
        name='bankId'
        label='Bank'
        placeholder='Search Bank'
        disabled={Boolean(searchParams?.companyPaymentTypeId)}
        options={banks.filter((bank) => bank.locations?.length).map((bank) => bank.companyId)}
        getOptionLabel={(bankId) => {
          const bank = banks.find((b) => b.companyId === bankId)
          return paymentTypeToCreateFrom?.bank?.name ?? bank?.name ?? ""
        }}
        onInputChange={setBankSearchTerm}
        defaultEmptyState={""}
        loading={isBanksLoading}
      />

      <BankLocationSelector bankId={watchedBankId} name='bankLocationId' label='Bank Location' />

      <BankDetailsFields />
    </Box>
  )
}

export default BankingMethodFields
